import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ProspectusService } from 'src/app/services/prospectus.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ApplicationType } from '../../models/application-type.enum';
import { PageOptions, Tab } from '../../models/interfaces';
import {
  ProductDocument,
  Prospectus,
} from 'src/app/models/prospectus-shareholder.model';
import { keyframes } from '@angular/animations';

@Component({
    selector: 'app-prospectus',
    templateUrl: './prospectus.component.html',
    styleUrls: ['./prospectus.component.scss'],
    standalone: false
})
export class ProspectusComponent implements OnInit, AfterViewInit {
  public pageOptions: PageOptions = {
    title: '',
    description: '',
    disclosures: null,
    eReviewNumber: null,
    renderNavBar: false,
    tabs: [],
    selectedTab: 0,
    applicationId: null,
  };
  public loading = true;
  public searchControl: UntypedFormControl = new UntypedFormControl();

  private allData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public filteredData: Observable<any[]>;
  private sortSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public onAnnuitiesPage: boolean = false; // flag for prospectus/annuities logic
  public onFundsPage: boolean = false; // flag for funds page logic
  public prospectusInformation: Prospectus;
  public productDocuments: ProductDocument[] = [];
  public showFamilyAlert: boolean = false;
  public alertMessage: string = '';
  constructor(
    private prospectusService: ProspectusService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  private sortFn = (a, b) => {
    return this.getFundName(a).localeCompare(this.getFundName(b));
  };

  ngOnInit() {
    this.pageOptions.title = this.route.snapshot.data.title || '';
    this.pageOptions.description = this.route.snapshot.data.description || '';
    this.pageOptions.tabs = this.route.snapshot.data.tabs || [];
    this.pageOptions.renderNavBar = this.route.snapshot.data.renderNavBar;
    this.pageOptions.disclosures = this.route.snapshot.data.disclosures;
    this.pageOptions.eReviewNumber = this.route.snapshot.data.eReviewNumber;
    this.pageOptions.applicationId = this.route.snapshot.data.applicationId;

    this.onAnnuitiesPage = this.route.routeConfig.path.includes(
      'prospectus/annuities'
    ); // flag for annuities page
    this.onFundsPage = this.route.routeConfig.path.includes('prospectus/funds'); // flag for funds page

    // let haveFundTypeQueryParam = window.location.href.toString().toLowerCase().includes('fundtype=');
    let fundTypeIsInvalid = false;
    const haveProductQueryParam = window.location.href
      .toString()
      .toLowerCase()
      .includes('product=');

    // annuities page requires product type param in URL, funds page requires fund type param
    // if ((this.onAnnuitiesPage && !haveProductQueryParam) || (this.onFundsPage && !fundTypeIsEmptyOrValid)) {
    if (this.onAnnuitiesPage && !haveProductQueryParam) {
      this.router.navigate(['error']);
    }

    // funds page: set up tabs
    if (this.onFundsPage && fundTypeIsInvalid) {
      let foundMatchingFundType = false;

      for (let i = 0; i < this.pageOptions.tabs.length; i++) {
        if (
          this.pageOptions.tabs[i].type ===
          this.route.snapshot.queryParams.fundtype
        ) {
          this.pageOptions.selectedTab = i;
          foundMatchingFundType = true;
          break;
        }
      }
      // dont load data for invalid fund type
      if (
        !foundMatchingFundType &&
        window.location.href.toString().toLowerCase().includes('fundtype=')
      ) {
        // this.pageOptions.tabs = [];
        fundTypeIsInvalid = true;
      }
    }

    if (
      (!this.onAnnuitiesPage && !this.onFundsPage) ||
      (this.onAnnuitiesPage && haveProductQueryParam) ||
      (this.onFundsPage && !fundTypeIsInvalid)
    ) {
      this.loadData();
    }
  }

  ngAfterViewInit() {
    this.handleMoreTab();
  }

  ngAfterViewChecked() {
    this.setUpTabARIAAttributes();
  }

  private loadData(): void {
    this.loading = true;
    const appTypeParam = this.getApplicationType();
    this.checkFundTypeUrlParam();
    const fundTypeParam = this.getCurrentTab()
      ? this.getCurrentTab().type
      : null;
    const productParam = this.route.snapshot.queryParams['product'] || null;

    this.prospectusService
      .getFundsDocuments(appTypeParam, fundTypeParam, productParam)
      .subscribe((res) => {
        this.loading = false;
        this.allData.next(res);

        if (this.onAnnuitiesPage) {
          this.prospectusInformation = res as Prospectus;
          this.setUpAnnuitiesTabs();
        }

        this.filterData();
      });
  }

  public filterData() {
    this.filteredData = combineLatest([
      this.searchControl.valueChanges.pipe(debounceTime(350), startWith('')),
      this.allData.asObservable(),
      this.sortSubject.asObservable(),
    ]).pipe(
      map(([filterString, prospectusData, sortOrder]) => {
        let filteredData;

        if (this.onAnnuitiesPage) {
          // filter first by family, then by search box input
          let fundFamilyFromURL = this.route.snapshot.queryParams.family || '';
          fundFamilyFromURL = fundFamilyFromURL.replace('%20', ' '); // handle any escaped whitespace in URL

          let prospectusArray = Object.values(
            (prospectusData as Prospectus).fundsDocuments
          );
          filteredData =
            fundFamilyFromURL && fundFamilyFromURL.length > 0
              ? prospectusArray.filter(
                (prosp) =>
                  prosp.familyName?.toLowerCase() ===
                  fundFamilyFromURL?.toLowerCase()
              )
              : prospectusArray;

          filteredData =
            filterString && filterString.length > 0
              ? filteredData.filter((prosp) =>
                this.getFundName(prosp, true)
                  .toLowerCase()
                  .includes(filterString.toLowerCase())
              )
              : filteredData;
        } else {
          // filter by search box input
          filteredData =
            filterString && filterString.length > 0
              ? prospectusData.filter((prosp) =>
                this.getFundName(prosp, true)
                  .toLowerCase()
                  .includes(filterString.toLowerCase())
              )
              : prospectusData;
        }

        filteredData.sort((a, b) =>
          sortOrder ? this.sortFn(a, b) : this.sortFn(b, a)
        );
        return filteredData;
      })
    );
  }

  private setUpAnnuitiesTabs() {
    let foundFamilyParam = false;
    let productQueryParam = this.route.snapshot.queryParams.product;
    let familyQueryParam = this.route.snapshot.queryParams.family;

    this.addSuperscript();

    if (familyQueryParam) {
      this.prospectusInformation.fundFamilies.forEach((family) => {
        if (family.toLowerCase() === familyQueryParam.toLowerCase()) {
          foundFamilyParam = true;
        }
      });
    }

    this.pageOptions.tabs = productQueryParam
      ? this.prospectusInformation.fundFamilies.map((family) => {
        let tab: Tab = {
          id: `${family}-tab`,
          family: family,
          label: family,
          applicationId: ApplicationType.FILI,
        };
        return tab;
      })
      : [];

    if (!window.location.href.toString().includes('family')) {
      // go to first tab (i.e. Fidelity) by default
      this.pageOptions.selectedTab = null;
      this.changeTab(0);
    } else {
      for (let i = 0; i < this.pageOptions.tabs.length; i++) {
        if (this.route.snapshot.queryParams.product &&
          (this.pageOptions.tabs[i].family.replace('%20', ' ').toLowerCase() === this.route.snapshot.queryParams.family.toLowerCase())
        ) {
          this.pageOptions.selectedTab = i;
          break;
        }
      }
    }
    if (productQueryParam && familyQueryParam && !foundFamilyParam) {
      this.alertMessage = `Provided family '${familyQueryParam}' doesn't have a valid information, Please try selecting any avaiable actions below.`,
        this.showFamilyAlert = true;
      this.changeTab(0);
    }
  }

  showTab(event) {
    if (event.detail.currentTab && event.detail.previousTab) {
      event.detail.currentTab.id !== event.detail.previousTab.id ? this.changeTab(event.detail.currentTab.id) : null;
    }
  }

  changeTab(tab: number): void {
    this.searchControl.reset();
    if ((this.pageOptions.selectedTab !== tab && this.pageOptions.tabs[tab])) {
      this.showFamilyAlert = false;
    }
    if ((this.pageOptions.selectedTab !== tab && this.pageOptions.tabs[tab]) || (this.showFamilyAlert && this.pageOptions.selectedTab === tab && this.pageOptions.tabs[tab])) {
      this.pageOptions.selectedTab = tab;
      // this.allData.next([]);
      let queryParams = this.onAnnuitiesPage
        ? {
          product: this.prospectusInformation.productCode,
          family: this.pageOptions.tabs[tab].family,
        }
        : { fundtype: this.pageOptions.tabs[tab].type };
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
        })
        .then(() => {
          if (!this.onAnnuitiesPage) {
            this.loadData();
          }
          this.filterData();
        });
    }
  }

  private getCurrentTab(): Tab | null {
    return this.pageOptions.selectedTab >= 0 &&
      this.pageOptions.tabs[this.pageOptions.selectedTab]
      ? this.pageOptions.tabs[this.pageOptions.selectedTab]
      : null;
  }

  private getApplicationType(): ApplicationType {
    const currentTab: Tab | null = this.getCurrentTab();
    return currentTab && currentTab.applicationId
      ? currentTab.applicationId
      : this.pageOptions.applicationId;
  }

  isAscSort(): boolean {
    return this.sortSubject.getValue();
  }

  toggleSort(): void {
    this.sortSubject.next(!this.sortSubject.getValue());
  }

  getRedirect(securityId: string, documentType: string): string {
    return this.prospectusService.getRedirect(
      securityId,
      documentType,
      this.getApplicationType()
    );
  }

  trackByFn = (index: number, item: any) => {
    return this.getCusip(item);
  };

  public generateTabId(family: string): string {
    return family.replace(' ', '-');
  }

  public generatePanelId(family: string): string {
    let tabId = this.generateTabId(family);
    let panelId = tabId.replace('pvd-tab', 'pvd-tab-panel');
    return panelId;
  }

  setUpTabARIAAttributes(id?: string) {
    let pvdTabCollection = document
      .getElementsByTagName('pvd-tabs')[0]
      .getElementsByTagName('pvd-tab');

    for (let i = 0; i < pvdTabCollection.length; i++) {
      let pvdTab = pvdTabCollection[i];
      let shadowTab = pvdTab.shadowRoot.getElementById(
        `pvd-tab-generated-${i}`
      );
      if (shadowTab) {
        let idString = `pvd-tab-generated-${i}`;
        shadowTab.setAttribute('aria-controls', idString);
        shadowTab.setAttribute('aria-label', idString);
      }
    }
  }

  getFundName(prospectus: any, clean: boolean = false): string {
    if (!prospectus) {
      return '';
    }
    switch (this.getApplicationType()) {
      case ApplicationType.SEC:
        let fundName = '';
        if (
          prospectus.fundInformation &&
          prospectus.fundInformation.portfolioName
        ) {
          fundName = prospectus.fundInformation.portfolioName;
        } else if (
          prospectus.fundInformation &&
          prospectus.fundInformation.fmdbSubAccountId
        ) {
          fundName = prospectus.fundInformation.longName || '';
        } else if (
          prospectus.fundInformation &&
          prospectus.fundInformation.legalName
        ) {
          fundName = clean
            ? prospectus.fundInformation._cleanLegalName
            : prospectus.fundInformation.legalName || '';
        } else {
          fundName = this.getNameFromDocuments(prospectus.documents);
        }
        return fundName;
      case ApplicationType.FILI:
      case ApplicationType.FILI_SEC:
        return prospectus.fundInformation.longName || '';
      default:
        return clean
          ? prospectus.fundInformation._cleanLegalName
          : prospectus.fundInformation.legalName || '';
    }
  }

  getCusip(prospectus: any) {
    if (prospectus.fundInformation?.cusip) {
      return prospectus.fundInformation.cusip;
    }
    for (const key of Object.keys(prospectus.documents)) {
      if (prospectus.documents[key]?.securityId) {
        return prospectus.documents[key].securityId;
      }
    }
    return null;
  }

  getNameFromDocuments(documents: any) {
    for (const key of Object.keys(documents)) {
      if (documents[key]?.fundName) {
        return documents[key].fundName;
      }
    }
    return null;
  }

  isShownColumn(columnName: string): boolean {
    switch (columnName) {
      case 'PROSPECTUS':
      case 'SAI':
        return [
          ApplicationType.RETAIL,
          ApplicationType.FILI,
          ApplicationType.WORKPLACE,
        ].includes(this.getApplicationType());
      case 'SHARE_REPORTS':
        return true;
      case 'NPORT':
        return [ApplicationType.SEC, ApplicationType.FILI_SEC].includes(
          this.getApplicationType()
        );
      default:
        return false;
    }
  }

  openCusipLinkPopup(link: string): void {
    this.openLinkInNewWindow(link);
  }

  openLinkInNewWindow(link: string): void {
    window.open(
      link,
      'popup',
      'width=1024,height=768,left=10,top=10,location=no,resizable=yes,menubar=no,status=no,toolbar=no,scrollbars=yes'
    );
  }

  checkFundTypeUrlParam() {
    let url = window.location.href.toString();
    if (url.includes('fundtype=')) {
      let urlFundTypeIndex = url.indexOf('fundtype=') + 9;
      let fundType = url.slice(urlFundTypeIndex, url.length).toUpperCase();
      for (let i = 0; i < this.pageOptions.tabs.length; i++) {
        if (this.pageOptions.tabs[i].type === fundType) {
          this.changeTab(i);
        }
      }
    }
  }

  addSuperscript() {
    let productDocs = this.prospectusInformation.productsDocuments;
    let pdEntries = Object.entries(productDocs);

    pdEntries.map((p, i) => {
      let label = p[0];
      let values: ProductDocument = p[1];
      if (label.includes('IncomeSM', 0)) {
        label = label.replace('IncomeSM', 'Income<sup>SM</sup>');
      } else if (label.includes('AnnuitySM', 0)) {
        label = label.replace('AnnuitySM', 'Annuity<sup>SM</sup>');
      }
      values.labelKey = label;
      this.productDocuments.push(values);
    });
  }

  handleMoreTab() {
    let pvdTabs = document.getElementsByTagName('pvd-tabs');
    let truncatedMenu =
      pvdTabs[0].shadowRoot.children[1].children[0].children[1];
    let optionsNodes = truncatedMenu.children[0];

    optionsNodes.addEventListener('click', function () {
      let optionChildren = optionsNodes.children;

      for (let i = 1; i < optionChildren.length; i++) {
        let option = optionChildren[i];
        let indexValString = option.getAttribute('value');
        let indexVal = indexValString.substr(
          indexValString.length - 1,
          indexValString.length
        );

        // below trying to use the extracted index of the truncated tabs to pass to the changeTab function
        // option.setAttribute('value', indexVal);
        // option.setAttribute('onClick', 'changeTab(indexVal)');
      }
    });
    // issue below is that the change event doesn't target any of the options
    optionsNodes.addEventListener('change', (event) => {
      // console.log(event);
      // console.log(event.composedPath());
    });
  }
}